import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface FlowForm {
  id: string;
  expected_date: string;
  amount_tnd: string;
  label: string;
  certain: boolean;
}

interface Bucket {
  bucket_label: string;
  bucket_start: string;
  bucket_end: string;
  inflows_millimes: number;
  outflows_millimes: number;
  net_change_millimes: number;
  closing_balance_millimes: number;
  is_below_threshold: boolean;
  uncertain_amount_millimes: number;
}

interface TresorierResult {
  horizon: '13w' | '12m';
  starting_balance_millimes: number;
  ending_balance_millimes: number;
  min_balance_millimes: number;
  min_balance_bucket: string;
  alert_buckets: string[];
  buckets: Bucket[];
  dso_days: number | null;
  risk_level: 'none' | 'low' | 'medium' | 'high' | 'critical';
}

const RISK_STYLE: Record<TresorierResult['risk_level'], string> = {
  none: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  low: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  medium: 'bg-amber-100 text-amber-900 border-amber-300',
  high: 'bg-orange-100 text-orange-900 border-orange-300',
  critical: 'bg-red-100 text-red-900 border-red-300',
};

const SAMPLE_INFLOWS: FlowForm[] = [
  { id: 'in1', expected_date: '2026-05-15', amount_tnd: '2000.000', label: 'Client ACME', certain: true },
  { id: 'in2', expected_date: '2026-05-30', amount_tnd: '5000.000', label: 'Client Sahel', certain: false },
];
const SAMPLE_OUTFLOWS: FlowForm[] = [
  { id: 'out1', expected_date: '2026-05-10', amount_tnd: '1500.000', label: 'Fournisseur STEG', certain: true },
  { id: 'out2', expected_date: '2026-06-28', amount_tnd: '3000.000', label: 'Acompte IS', certain: true },
];

export function TresorierPage() {
  const [bankAccount, setBankAccount] = useState('532');
  const [startingBalance, setStartingBalance] = useState('5000.000');
  const [startingDate, setStartingDate] = useState('2026-05-04');
  const [horizon, setHorizon] = useState<'13w' | '12m'>('13w');
  const [alertThreshold, setAlertThreshold] = useState('0.000');
  const [inflows, setInflows] = useState<FlowForm[]>(SAMPLE_INFLOWS);
  const [outflows, setOutflows] = useState<FlowForm[]>(SAMPLE_OUTFLOWS);
  const [withReceivables, setWithReceivables] = useState(false);
  const [caHt, setCaHt] = useState('100000.000');
  const [creances, setCreances] = useState('25000.000');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function makeFlowSetters(
    set: typeof setInflows,
    prefix: string,
  ) {
    return {
      add: () => set((p) => [...p, { id: `${prefix}${p.length + 1}`, expected_date: startingDate, amount_tnd: '0.000', label: '', certain: true }]),
      remove: (i: number) => set((p) => p.filter((_, idx) => idx !== i)),
      update: (i: number, patch: Partial<FlowForm>) => set((p) => p.map((f, idx) => (idx === i ? { ...f, ...patch } : f))),
    };
  }

  const inflowOps = makeFlowSetters(setInflows, 'in');
  const outflowOps = makeFlowSetters(setOutflows, 'out');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload: Record<string, unknown> = {
        bank_account_sce_code: bankAccount,
        starting_balance_millimes: tndStringToMillimes(startingBalance),
        starting_date: startingDate,
        horizon,
        alert_threshold_millimes: tndStringToMillimes(alertThreshold),
        inflows: inflows.map((f) => ({
          id: f.id,
          expected_date: f.expected_date,
          amount_millimes: tndStringToMillimes(f.amount_tnd),
          label: f.label || `Flow ${f.id}`,
          certain: f.certain,
        })),
        outflows: outflows.map((f) => ({
          id: f.id,
          expected_date: f.expected_date,
          amount_millimes: tndStringToMillimes(f.amount_tnd),
          label: f.label || `Flow ${f.id}`,
          certain: f.certain,
        })),
      };
      if (withReceivables) {
        payload.receivables = {
          ca_ht_millimes: tndStringToMillimes(caHt),
          creances_clients_millimes: tndStringToMillimes(creances),
        };
      }
      const res = await api.invokeAgent('tresorier', { payload, fiscal_reference_date: startingDate });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Trésorier"
      module="Extension · Phase 4"
      model="Sonnet 4.6"
      description={
        <>
          Projection cashflow rolling 13 semaines ou 12 mois — alertes seuil + DSO si receivables
          fournis. Catégorise inflows/outflows par bucket, identifie le bucket à plus bas solde.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Paramètres de projection">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Compte SCE bancaire">
              <input className={inputCls + ' font-mono'} value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} required />
            </Field>
            <Field label="Solde de départ (TND)">
              <input className={inputCls + ' font-mono'} value={startingBalance} onChange={(e) => setStartingBalance(e.target.value)} required />
            </Field>
            <Field label="Date de départ">
              <input type="date" className={inputCls} value={startingDate} onChange={(e) => setStartingDate(e.target.value)} required />
            </Field>
            <Field label="Horizon">
              <select className={inputCls} value={horizon} onChange={(e) => setHorizon(e.target.value as '13w' | '12m')}>
                <option value="13w">13 semaines</option>
                <option value="12m">12 mois</option>
              </select>
            </Field>
            <Field label="Seuil d'alerte (TND)" hint="Bucket avec solde < seuil → alerte">
              <input className={inputCls + ' font-mono'} value={alertThreshold} onChange={(e) => setAlertThreshold(e.target.value)} />
            </Field>
            <div className="flex flex-col gap-1 pt-1">
              <label className="text-sm font-medium flex items-center gap-2">
                <input type="checkbox" checked={withReceivables} onChange={(e) => setWithReceivables(e.target.checked)} />
                Inclure DSO
              </label>
              {withReceivables && (
                <div className="grid grid-cols-2 gap-2">
                  <Field label="CA HT (TND)"><input className={inputCls + ' font-mono'} value={caHt} onChange={(e) => setCaHt(e.target.value)} /></Field>
                  <Field label="Créances clients (TND)"><input className={inputCls + ' font-mono'} value={creances} onChange={(e) => setCreances(e.target.value)} /></Field>
                </div>
              )}
            </div>
          </div>
        </FormCard>

        <FlowsCard
          title={`Encaissements prévus (${inflows.length})`}
          flows={inflows}
          ops={inflowOps}
          variant="in"
        />
        <FlowsCard
          title={`Décaissements prévus (${outflows.length})`}
          flows={outflows}
          ops={outflowOps}
          variant="out"
        />

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Projection…' : 'Projeter la trésorerie'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <TresorierResultView result={result as TresorierResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function FlowsCard({
  title,
  flows,
  ops,
  variant,
}: {
  title: string;
  flows: FlowForm[];
  ops: { add: () => void; remove: (i: number) => void; update: (i: number, patch: Partial<FlowForm>) => void };
  variant: 'in' | 'out';
}) {
  return (
    <FormCard
      title={title}
      action={
        <button type="button" onClick={ops.add} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">
          + Ajouter
        </button>
      }
    >
      <div className="space-y-2">
        {flows.map((f, idx) => (
          <div key={idx} className={`border rounded p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-end ${variant === 'in' ? 'border-emerald-200 bg-emerald-50/30' : 'border-rose-200 bg-rose-50/30'}`}>
            <Field label="ID"><input className={inputCls + ' font-mono'} value={f.id} onChange={(e) => ops.update(idx, { id: e.target.value })} /></Field>
            <Field label="Date prévue"><input type="date" className={inputCls} value={f.expected_date} onChange={(e) => ops.update(idx, { expected_date: e.target.value })} /></Field>
            <Field label="Montant (TND)"><input className={inputCls + ' font-mono'} value={f.amount_tnd} onChange={(e) => ops.update(idx, { amount_tnd: e.target.value })} /></Field>
            <Field label="Libellé"><input className={inputCls} value={f.label} onChange={(e) => ops.update(idx, { label: e.target.value })} /></Field>
            <label className="text-sm pt-5"><input type="checkbox" checked={f.certain} onChange={(e) => ops.update(idx, { certain: e.target.checked })} /> Certain</label>
            <button type="button" onClick={() => ops.remove(idx)} className="text-xs text-red-700 hover:text-red-900 self-end">Supprimer</button>
          </div>
        ))}
      </div>
    </FormCard>
  );
}

function TresorierResultView({ result }: { result: TresorierResult }) {
  const sign = result.ending_balance_millimes - result.starting_balance_millimes;
  return (
    <div className="space-y-5">
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Synthèse</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <Stat label="Solde départ" value={formatMillimes(result.starting_balance_millimes)} />
          <Stat label="Solde fin de période" value={formatMillimes(result.ending_balance_millimes)} highlight />
          <Stat label="Δ Variation" value={(sign >= 0 ? '+' : '') + formatMillimes(sign)} />
          <Stat label="Solde min (au plus bas)" value={formatMillimes(result.min_balance_millimes)} />
        </div>
      </section>

      <section className="flex items-center gap-3 flex-wrap">
        <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded border ${RISK_STYLE[result.risk_level]}`}>
          Risque {result.risk_level}
        </span>
        {result.alert_buckets.length > 0 && (
          <span className="text-xs text-red-800">
            ⚠ {result.alert_buckets.length} bucket(s) en alerte : {result.alert_buckets.slice(0, 5).join(', ')}{result.alert_buckets.length > 5 ? '…' : ''}
          </span>
        )}
        {result.dso_days !== null && (
          <span className="text-xs text-taupe">DSO : <strong className="text-ink">{result.dso_days.toFixed(1)} jours</strong></span>
        )}
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Buckets ({result.buckets.length})</div>
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-taupe border-b border-border">
                <th className="px-2 py-2 text-left">Bucket</th>
                <th className="px-2 py-2 text-left">Période</th>
                <th className="px-2 py-2 text-right">Encaissements</th>
                <th className="px-2 py-2 text-right">Décaissements</th>
                <th className="px-2 py-2 text-right">Net</th>
                <th className="px-2 py-2 text-right">Solde clôture</th>
              </tr>
            </thead>
            <tbody>
              {result.buckets.map((b) => (
                <tr key={b.bucket_label} className={`border-b border-border/50 ${b.is_below_threshold ? 'bg-red-50/40' : ''}`}>
                  <td className="px-2 py-1.5 font-mono">{b.bucket_label}{b.bucket_label === result.min_balance_bucket && <span className="ml-1 text-xs text-red-700">↓min</span>}</td>
                  <td className="px-2 py-1.5 font-mono text-xs text-taupe">{b.bucket_start} → {b.bucket_end}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-emerald-800">{b.inflows_millimes > 0 ? formatMillimes(b.inflows_millimes, { withSymbol: false }) : '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-rose-800">{b.outflows_millimes > 0 ? formatMillimes(b.outflows_millimes, { withSymbol: false }) : '—'}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${b.net_change_millimes < 0 ? 'text-rose-800' : b.net_change_millimes > 0 ? 'text-emerald-800' : ''}`}>
                    {b.net_change_millimes !== 0 ? (b.net_change_millimes > 0 ? '+' : '') + formatMillimes(b.net_change_millimes, { withSymbol: false }) : '—'}
                  </td>
                  <td className={`px-2 py-1.5 text-right font-mono font-bold ${b.is_below_threshold ? 'text-red-800' : ''}`}>{formatMillimes(b.closing_balance_millimes, { withSymbol: false })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-2 rounded border text-center ${highlight ? 'border-gold bg-gold/10' : 'border-border bg-white'}`}>
      <div className="text-xs uppercase tracking-wider text-taupe">{label}</div>
      <div className={`font-mono text-base ${highlight ? 'font-bold' : ''}`}>{value}</div>
    </div>
  );
}
